import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanTipTapEditor } from '@/components';
import { type GenerateBroadcastFormData, generateBroadcastSchema } from '../../validations/generateBroadcast';
import { generateBroadcastEmail } from '@/lib/api/email-flows/api';
import { useUser } from '@/queries';
import { useBroadcastEditorStore } from '../../stores';
import { stanAnalytics } from '@/lib';
import { stanNotify } from '@/lib/stanNotify';
import { cleanGeneratedEmailBody } from '../../logic/emailGeneration.logic';

const GENERATION_TIMEOUT_MS = 12000; // 12 seconds

type Props = {
    onComplete: () => void;
};

export const GenerateBroadcastEmailForm: FC<Props> = ({ onComplete }) => {
    const { user } = useUser();
    const [isGenerating, setIsGenerating] = useState(false);
    const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);
    const setGeneratedDraft = useBroadcastEditorStore(state => state.setGeneratedDraft);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<GenerateBroadcastFormData>({
        resolver: zodResolver(generateBroadcastSchema),
        defaultValues: {
            prompt: '',
        },
    });

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            setShowTimeoutMessage(false);
        };
    }, []);

    const onSubmit = async (data: GenerateBroadcastFormData) => {
        if (!user) return;

        let timeoutTimer: NodeJS.Timeout | null = null;

        try {
            setIsGenerating(true);
            setShowTimeoutMessage(false);

            // Show timeout message after timeout period
            timeoutTimer = setTimeout(() => {
                setShowTimeoutMessage(true);
            }, GENERATION_TIMEOUT_MS);

            // Track analytics - generation started
            stanAnalytics('generate_email_template_with_stanley_viewed', {
                meta: { user_id: user.user_id, username: user.username },
            });

            const response = await generateBroadcastEmail({
                chat_id: null,
                question: data.prompt,
                user_id: Number(user.user_id),
            });

            if (timeoutTimer) clearTimeout(timeoutTimer);

            // Clean the generated body to remove excessive br tags
            const cleanedBody = cleanGeneratedEmailBody(response.body);

            // Track analytics - first draft ready
            stanAnalytics('generate_email_template_with_stanley_first_draft_ready', {
                meta: { user_id: user.user_id, username: user.username },
                props: {
                    prompt: data.prompt,
                    response_id: response.response_id,
                    subject: response.subject,
                    body: cleanedBody,
                },
            });

            // Store the generated draft
            setGeneratedDraft({
                prompt: data.prompt,
                response_id: response.response_id,
                subject: response.subject,
                body: cleanedBody,
                feedback_status: 0,
            });

            onComplete();
        } catch {
            stanNotify({
                type: 'error',
                title: 'Something went wrong!',
                text: 'Seems like Stanley is having some trouble generating the first draft. Please try again later!',
                duration: 4000,
            });
        } finally {
            if (timeoutTimer) clearTimeout(timeoutTimer);
            setIsGenerating(false);
            setShowTimeoutMessage(false);
        }
    };

    const handleSkip = () => {
        onComplete();
    };

    return (
        <div className='md:bg-canvas md:shadow-stan flex w-full flex-col rounded-2xl sm:max-w-lg md:gap-6 md:p-6'>
            <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6'>
                <Controller
                    name='prompt'
                    control={control}
                    render={({ field }) => (
                        <StanTipTapEditor
                            {...field}
                            placeholder='Example: Send a thank-you email to everyone who attended our webinar and ...'
                            error={!!errors.prompt}
                            disabled={isGenerating}
                            hideToolbar
                            className='min-h-48'
                        />
                    )}
                />

                {errors.prompt && (
                    <p className='text-body-xs text-negative -mt-4 font-semibold'>{errors.prompt.message}</p>
                )}

                {showTimeoutMessage && (
                    <p className='text-body-xs text-negative -mt-4 font-semibold'>
                        Taking longer than expected. Click on &ldquo;No Thanks, I&apos;ll do it myself&rdquo; to skip
                        this step.
                    </p>
                )}

                <div className='flex flex-col'>
                    <StanButton
                        name='generate-email-template-with-stanley-ask-stanley-button'
                        type='submit'
                        loading={isGenerating}
                        disabled={isGenerating}
                        className='w-full'
                    >
                        Generate First Draft
                    </StanButton>

                    <StanButton
                        name='generate-email-template-with-stanley-no-thanks-button'
                        variant='ghost'
                        type='button'
                        onClick={handleSkip}
                        disabled={!showTimeoutMessage && isGenerating}
                    >
                        No Thanks, I&apos;ll do it myself
                    </StanButton>
                </div>
            </form>
        </div>
    );
};
